import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

export default function Finance() {
  useSEO({
    title: 'Home Financing & Mortgage Calculator | Northern California | Jeffery Long eXp Realty',
    description: 'Get pre-approved for your home loan with competitive rates and local underwriting. Mortgage calculator, FHA loans, VA loans, conventional financing. Expert guidance from Jeffery Long serving Sacramento, El Dorado Hills, Northern California. CA DRE #02234432',
    keywords: 'home financing, mortgage calculator, home loans, FHA loans, VA loans, conventional loans, mortgage pre-approval, Sacramento mortgage, Northern California financing, real estate financing',
    canonical: '/finance',
    ogTitle: 'Home Financing & Mortgage Calculator | Northern California',
    ogDescription: 'Get pre-approved with low rates and local underwriting. Calculate your mortgage payments and explore financing options.',
    ogImage: 'https://public.readdy.ai/ai/img_res/5e3cf08a729252336c2546a4053162af.jpg',
    schema: {
      "@context": "https://schema.org",
      "@type": "FinancialService",
      "name": "Home Financing Services",
      "provider": {
        "@type": "RealEstateAgent",
        "name": "Jeffery Long - eXp Realty",
        "telephone": "+1-916-257-8244"
      },
      "areaServed": {
        "@type": "State",
        "name": "California"
      },
      "description": "Comprehensive home financing services including mortgage pre-approval, FHA loans, VA loans, conventional loans, jumbo loans, and refinancing options."
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-green-900 to-green-700 text-white py-20">
          <div className="absolute inset-0 bg-black/30"></div>
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://readdy.ai/api/search-image?query=professional%20mortgage%20financing%20calculator%20and%20documents%20on%20modern%20desk%20with%20clean%20business%20background%20for%20home%20loans&width=1200&height=600&seq=finance-hero&orientation=landscape')`
            }}
          ></div>
          <div className="relative container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Home Financing</h1>
            <p className="text-xl md:text-2xl mb-8">Get Pre-Approved with Low Rates and Local Underwriting</p>
            <button className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors">
              Start Application
            </button>
          </div>
        </section>

        {/* Loan Calculator */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Mortgage Calculator</h2>
              <div className="bg-white rounded-lg shadow-lg p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Home Price</label>
                        <input type="number" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="$500,000" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Down Payment</label>
                        <input type="number" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="$100,000" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Interest Rate (%)</label>
                        <input type="number" step="0.01" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="6.5" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Loan Term (Years)</label>
                        <select className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500">
                          <option value="30">30 Years</option>
                          <option value="15">15 Years</option>
                          <option value="20">20 Years</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="bg-green-50 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold mb-4">Monthly Payment Breakdown</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span>Principal & Interest:</span>
                        <span className="font-semibold">$2,528</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Property Tax:</span>
                        <span className="font-semibold">$625</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Home Insurance:</span>
                        <span className="font-semibold">$125</span>
                      </div>
                      <div className="flex justify-between">
                        <span>PMI:</span>
                        <span className="font-semibold">$167</span>
                      </div>
                      <hr className="my-2" />
                      <div className="flex justify-between text-lg font-bold">
                        <span>Total Monthly Payment:</span>
                        <span>$3,445</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Loan Programs */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Loan Programs Available</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-home-4-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">Conventional Loans</h3>
                <p className="text-gray-600 mb-4">Traditional financing with competitive rates and flexible terms.</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Down payment as low as 3%</li>
                  <li>• Fixed or adjustable rates</li>
                  <li>• 15 or 30-year terms</li>
                </ul>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-government-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">FHA Loans</h3>
                <p className="text-gray-600 mb-4">Government-backed loans with lower down payment requirements.</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Down payment as low as 3.5%</li>
                  <li>• More flexible credit requirements</li>
                  <li>• Mortgage insurance required</li>
                </ul>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-medal-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">VA Loans</h3>
                <p className="text-gray-600 mb-4">Exclusive benefits for eligible veterans and service members.</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• No down payment required</li>
                  <li>• No mortgage insurance</li>
                  <li>• Competitive interest rates</li>
                </ul>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-plant-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">USDA Loans</h3>
                <p className="text-gray-600 mb-4">Rural development loans for eligible properties.</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• No down payment required</li>
                  <li>• Income and location restrictions</li>
                  <li>• Below-market interest rates</li>
                </ul>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-vip-crown-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">Jumbo Loans</h3>
                <p className="text-gray-600 mb-4">Financing for high-value properties above conforming limits.</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Loan amounts above $766,550</li>
                  <li>• Competitive rates</li>
                  <li>• Flexible terms available</li>
                </ul>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-refresh-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-semibold mb-3">Refinancing</h3>
                <p className="text-gray-600 mb-4">Lower your rate or access your home's equity.</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Rate and term refinance</li>
                  <li>• Cash-out refinance</li>
                  <li>• Streamline options</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Pre-Approval Process */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Get Pre-Approved in 3 Easy Steps</h2>
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">1</div>
                  <h3 className="text-xl font-semibold mb-4">Submit Application</h3>
                  <p className="text-gray-600">Complete our secure online application in just minutes.</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">2</div>
                  <h3 className="text-xl font-semibold mb-4">Document Review</h3>
                  <p className="text-gray-600">Our team will review your documents and verify your information.</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">3</div>
                  <h3 className="text-xl font-semibold mb-4">Get Approved</h3>
                  <p className="text-gray-600">Receive your pre-approval letter and start shopping with confidence.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Application Form */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-3xl font-bold text-center mb-8">Start Your Pre-Approval Application</h2>
                <form className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                    <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="First Name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                    <input type="text" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="Last Name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input type="email" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="Email Address" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <input type="tel" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="Phone Number" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Annual Income</label>
                    <input type="number" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="Annual Income" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Desired Loan Amount</label>
                    <input type="number" className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="Loan Amount" />
                  </div>
                  <div className="md:col-span-2">
                    <button type="submit" className="w-full bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-md text-lg font-semibold transition-colors">
                      Submit Pre-Approval Application
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-16 bg-green-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Questions About Financing?</h2>
            <p className="text-xl mb-8">Our mortgage specialists are here to help</p>
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
              <a href="tel:916-257-8244" className="bg-white text-green-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                Call (916) 257-8244
              </a>
              <a href="mailto:realestatedevelopmentauthority@gmail.com" className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-green-600 transition-colors">
                Email Us
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
